import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal, { RevealGroup, RevealItem } from "@/components/Reveal";

function Block({ index, title, children }) {
  return (
    <section className="border-b border-ink/12 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="font-mono text-xs tracking-[0.24em] text-teal">{index}</span>
            <h2 className="mt-4 font-serif text-2xl leading-snug tracking-tight text-ink md:text-3xl">
              {title}
            </h2>
          </div>
          <div className="md:col-span-8">{children}</div>
        </div>
      </div>
    </section>
  );
}

export default function Platform() {
  return (
    <div data-testid="platform-page">
      <PageIntro
        index="/engineering-architecture"
        eyebrow="Engineering architecture"
        titleLines={["Governance conditions,", "executable at the point", "of consequential action."]}
        lead="KATLAS makes role, policy, evidence-reference and receipt conditions executable before a request, approval, refusal, release, handoff or escalation proceeds. It records the governed action — not a copy of everyone's private data."
        meta={["Custody · Authority · Receipts", "Control Decision Envelope", "Shared proof & receipt layer"]}
      />

      <Block index="§ 01" title="Control Decision Envelope">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            A standardisable object consumed by a token, register, AI agent or workflow
            adapter before a consequential action executes — proving the configured
            authority, policy and evidence-reference conditions are satisfied. It does not
            replace token logic, custody, settlement or existing systems of record.
          </p>
        </Reveal>
        <RevealGroup className="mt-8 flex flex-wrap gap-2">
          {["allowed", "blocked", "review required", "released", "refused"].map((o) => (
            <RevealItem key={o}>
              <span className="border border-ink/25 px-3 py-1.5 font-mono text-xs text-ink/75">{o}</span>
            </RevealItem>
          ))}
        </RevealGroup>
      </Block>

      <Block index="§ 02" title="Role and authority model">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            Stakeholder-defined roles, mandates, scopes and purposes. Every consequential
            action has a visible authority gate: who may request, approve, refuse, share,
            release or escalate. Authority is delegated explicitly and bounded by design.
          </p>
        </Reveal>
      </Block>

      <Block index="§ 03" title="Evidence custody and references">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            Evidence remains with the accountable custodian. KATLAS carries metadata, roles,
            permissions and evidence references — never raw private data or withheld assets.
            A shared / withheld matrix makes the boundary explicit.
          </p>
        </Reveal>
      </Block>

      <Block index="§ 04" title="Policy and state gates">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            Stakeholder-defined policy and current-state conditions are evaluated at the
            point of action, producing one governed outcome. Institutional policy and
            authorised roles decide; KATLAS makes that decision boundary executable.
          </p>
        </Reveal>
      </Block>

      <Block index="§ 05" title="Receipts and verification">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            A public-safe receipt records who acted, when, under what authority, which
            evidence references were used and what was withheld, with a verification status.
            Full signed envelopes are not exposed publicly.
          </p>
        </Reveal>
      </Block>

      <Block index="§ 06" title="Integration architecture">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            Execution and interoperability adapters let existing systems consume the
            authorised outcome. AI may suggest, draft, summarise or structure; it must not
            approve, override authority gates or access unrestricted private data.
          </p>
        </Reveal>
      </Block>

      <Block index="§ 07" title="Showcase, governed-sandbox and pilot modes">
        <RevealGroup className="grid grid-cols-1 gap-px border border-ink/15 bg-ink/15 sm:grid-cols-3">
          {[
            { k: "Showcase", d: "Illustrative governed journeys explaining the pattern." },
            { k: "Governed sandbox", d: "Synthetic but realistic, with separate role identities and devices." },
            { k: "Pilot", d: "One bounded problem against a controlled system-of-record boundary." },
          ].map((m) => (
            <RevealItem key={m.k} className="bg-stone p-7">
              <h3 className="font-serif text-lg text-ink">{m.k}</h3>
              <p className="mt-3 font-sans text-sm leading-relaxed text-ink/70">{m.d}</p>
            </RevealItem>
          ))}
        </RevealGroup>
      </Block>

      <Block index="§ 08" title="Integration patterns">
        <Reveal>
          <p className="max-w-2xl font-sans text-base leading-relaxed text-ink/75 md:text-lg">
            Conceptual patterns only — REST APIs, backend-only authentication, execution
            adapters, and event / webhook integration where actually supported. Standards
            examples where relevant: ISO 20022 (value movement) and FHIR (health records).
            Simulated operation uses synthetic data; live-node operation is provisioned
            separately. No specific production APIs, connectors or certifications are claimed.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-8 border-l-2 border-teal pl-6">
          <p className="font-serif text-lg italic leading-snug text-ink md:text-xl">
            Institutional policy and authorised roles decide. KATLAS makes the decision
            boundary executable, verifiable and reusable.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/technical-appendix"
            data-testid="platform-appendix-link"
            className="group inline-flex items-center gap-2 border border-ink/25 px-6 py-3.5 font-sans text-sm text-ink transition-colors duration-300 hover:border-ink"
          >
            View the technical appendix
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </Link>
          <Link
            to="/company#contact"
            className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink"
          >
            Request a pilot
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </Link>
        </Reveal>
      </Block>
    </div>
  );
}
